import React, { useState, useEffect } from "react";
import { X, Send } from "lucide-react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion"; // Optional: Added for smooth entry/exit
import "react-toastify/dist/ReactToastify.css";

export default function EnquirePopup() {
    const [showPopup, setShowPopup] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: "",
    });

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (location.pathname.includes("/enquire")) {
            setShowPopup(true);
        }
    }, [location.pathname]);

    const handleClose = () => {
        setShowPopup(false);
        navigate(-1);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        toast.success("Thank you! We'll get back to you soon.", {
            position: "top-center",
            autoClose: 3000,
            theme: "colored",
        });
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => {
            setShowPopup(false);
            navigate(-1);
        }, 1000);
    };

    return (
        <AnimatePresence>
            {showPopup && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />

                    {/* Modal Container */}
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
                    >
                        {/* Header / Close Button */}
                        <button
                            onClick={handleClose}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={22} />
                        </button>

                        <div className="p-8">
                            <h2 className="text-2xl font-bold text-gray-800">
                                Enquire Now
                            </h2>
                            <p className="mt-2 text-sm text-gray-500">
                                Fill in your details and we’ll contact you
                                shortly.
                            </p>

                            <form
                                className="mt-6 space-y-4"
                                onSubmit={handleSubmit}
                            >
                                <div>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Your Name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition-all focus:border-red-600 focus:bg-white focus:ring-2 focus:ring-red-100"
                                    />
                                </div>

                                <div>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Your Email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition-all focus:border-red-600 focus:bg-white focus:ring-2 focus:ring-red-100"
                                    />
                                </div>

                                <div>
                                    <textarea
                                        name="message"
                                        placeholder="Your Message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows="4"
                                        required
                                        className="w-full resize-none rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition-all focus:border-red-600 focus:bg-white focus:ring-2 focus:ring-red-100"
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-700 py-3 text-sm font-bold text-white shadow-lg transition-all hover:bg-red-800 active:scale-95"
                                >
                                    <Send size={18} />
                                    Send Enquiry
                                </button>
                            </form>
                        </div>

                        {/* Bottom Accent Line */}
                        <div className="h-1.5 w-full bg-red-700" />
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
